/**
 * Legacy sqlite `'' → 'main'` schema fold for renderer-side MetaInfo lookups.
 *
 * Pre-2026-05-31 sqlite MetaInfo (old exports, teammate backups) keyed
 * entries with `schema: ''`; current lookups pass the `'main'` convention.
 * Rather than rewriting user data at the import boundary — the MetaInfo store
 * is engine-blind and `''` is a LEGITIMATE schema key for Kusto — we fold on
 * the lookup side: a `'main'` lookup also accepts a `''` entry. The fold is
 * one-directional (a genuine `''` lookup only matches `''` entries), so Kusto
 * keeps resolving. Mirrors the resolver-side fold in
 * `src/features/sql-resolver/metaInfoHints/metaInfoHintProvider.ts`
 * (`addMainSchemaAliases`).
 */
export const schemaKeyMatches = (entrySchema: string, lookupSchema: string): boolean =>
  entrySchema === lookupSchema || (lookupSchema === 'main' && entrySchema === '');

/**
 * Find a MetaInfo entry by schema with exact-match preference, folding the
 * legacy `'' → 'main'` alias only when no exact-schema entry matches — so a
 * genuine `'main'` entry always wins over a legacy `''` alias (non-clobbering),
 * regardless of array order. `matchesRest` covers the remaining identity
 * fields (table / column / path / …).
 */
export const findWithSchemaFold = <T extends { schema: string }>(
  entries: ReadonlyArray<T> | undefined,
  lookupSchema: string,
  matchesRest: (entry: T) => boolean,
): T | undefined => {
  if (!entries || entries.length === 0) return undefined;
  const exact = entries.find((e) => e.schema === lookupSchema && matchesRest(e));
  if (exact) return exact;
  if (lookupSchema === 'main') {
    return entries.find((e) => e.schema === '' && matchesRest(e));
  }
  return undefined;
};
